import React, {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from "react";
import FontAwesome from "react-fontawesome";
import { cx } from "./util";
import { useDetailsDismiss } from "./useDetailsDismiss";

const NavbarContext = createContext<{
  expanded?: boolean;
  onToggle?: () => void;
}>({});

type NavbarProps = {
  fluid?: boolean;
  fixedTop?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
} & React.HTMLAttributes<HTMLElement>;

function NavbarBase({
  fluid,
  fixedTop,
  expanded,
  onToggle,
  className,
  children,
  ...rest
}: NavbarProps) {
  // fluid/fixedTop swallowed: the daisy navbar is always fixed-top full-width,
  // matching rework's navbar.scala.html. Inline style (navColor) passes via rest.
  // min-h-[50px] overrides daisy's 4rem — Bootstrap 3's $navbar-height was 50px.
  return (
    <NavbarContext.Provider value={{ expanded, onToggle }}>
      <nav
        className={cx(
          "navbar min-h-[50px] fixed top-0 inset-x-0 z-50 shadow-sm",
          className,
        )}
        {...rest}
      >
        {children}
      </nav>
    </NavbarContext.Provider>
  );
}

const NavbarHeader = ({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cx("flex-1 flex items-center gap-2", className)} {...rest}>
    {children}
  </div>
);

function NavbarToggle({
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onToggle } = useContext(NavbarContext);
  return (
    <button
      type="button"
      // ml-auto: Bootstrap 3's .navbar-toggle floated right; render after
      // Brand so DOM/tab order matches the visual order.
      className={cx("btn btn-ghost nav:hidden ml-auto", className)}
      aria-label="Toggle navigation"
      onClick={onToggle}
      {...rest}
    >
      <FontAwesome name="bars" />
    </button>
  );
}

function NavbarBrand({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLElement>) {
  if (isValidElement<{ className?: string }>(children)) {
    return cloneElement(children, {
      className: cx("flex items-center", children.props.className, className),
      ...rest,
    });
  }
  return (
    <span className={cx("flex items-center", className)} {...rest}>
      {children}
    </span>
  );
}

function NavbarCollapse({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded } = useContext(NavbarContext);
  return (
    <div
      className={cx(
        "flex-none max-nav:absolute max-nav:top-full max-nav:inset-x-0 max-nav:bg-base-100 max-nav:shadow-md",
        !expanded && "max-nav:hidden",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- compound component (Object.assign) the rule can't recognize
export const Navbar = Object.assign(NavbarBase, {
  Header: NavbarHeader,
  Toggle: NavbarToggle,
  Brand: NavbarBrand,
  Collapse: NavbarCollapse,
});

type NavProps = {
  pullRight?: boolean;
  bsStyle?: string;
  // Swallowed (react-bootstrap API); shadows the rarely-used DOM onSelect.
  onSelect?: unknown;
} & Omit<React.HTMLAttributes<HTMLUListElement>, "onSelect">;

export function Nav({
  pullRight,
  bsStyle,
  onSelect,
  className,
  children,
  ...rest
}: NavProps) {
  if (bsStyle === "tabs") {
    // ShopWizard injects raw <li className="active"> children — keep bootstrap
    // markup; bootstrap-compat.css styles .nav-tabs from Task 9 on.
    return (
      <ul className={cx("nav nav-tabs", className)} {...rest}>
        {children}
      </ul>
    );
  }
  return (
    <ul
      className={cx(
        // nav:py-0 keeps the desktop navbar at Bootstrap 3's 50px — daisy's
        // menu padding otherwise stacks with the navbar's own. `nav` breakpoint
        // (1440px) matches the toggle/collapse so the row goes horizontal only
        // when it actually fits (review #42).
        "menu nav:menu-horizontal items-stretch nav:items-center gap-1 w-full nav:w-auto nav:py-0",
        className,
      )}
      {...rest}
    >
      {children}
    </ul>
  );
}

type NavItemProps = {
  disabled?: boolean;
} & React.LiHTMLAttributes<HTMLLIElement>;

export function NavItem({
  disabled,
  className,
  children,
  ...rest
}: NavItemProps) {
  return (
    <li
      role="presentation"
      className={cx(disabled && "disabled", className)}
      {...rest}
    >
      <a role="button">{children}</a>
    </li>
  );
}

type NavDropdownProps = {
  id?: string;
  title?: React.ReactNode;
} & Omit<React.LiHTMLAttributes<HTMLLIElement>, "title">;

export function NavDropdown({
  id,
  title,
  className,
  children,
  ...rest
}: NavDropdownProps) {
  const dismissRef = useDetailsDismiss();
  return (
    <li className={className || undefined} id={id} {...rest}>
      <details ref={dismissRef}>
        <summary>{title}</summary>
        <ul className="bg-base-100 rounded-box z-50 p-2 shadow w-56">
          {children}
        </ul>
      </details>
    </li>
  );
}
